import { createSelector, createSlice } from '@reduxjs/toolkit';
import type { State } from '@/reducers/types';
import { getDocumentsIds, updateDocumentId } from './actions';
import { DocumentId } from '@/types';
import { logout } from '@/features/auth';

export interface DocumentsIdsState {
  documentsIds: DocumentId[] | null;
  isLoading: boolean;
}

const initialState: DocumentsIdsState = {
  documentsIds: null,
  isLoading: false,
};

export const { name, reducer } = createSlice({
  name: 'documentsIds',
  initialState,
  reducers: {},
  extraReducers: (builder) => {
    builder
      .addCase(getDocumentsIds.pending, (state) => {
        state.isLoading = true;
      })
      .addCase(getDocumentsIds.fulfilled, (state, action) => {
        state.documentsIds = action.payload;
        state.isLoading = false;
      })
      .addCase(updateDocumentId.fulfilled, (state, action) => {
        if (state.documentsIds) {
          const idxToUpdate = state.documentsIds.findIndex(
            (document) => document.id === action.payload.id,
          );
          if (idxToUpdate !== -1) {
            state.documentsIds[idxToUpdate] = action.payload;
          }
        }
      })
      .addCase(logout.fulfilled, () => initialState);
  },
});

export const selectDocumentsIds = (state: State) => state[name].documentsIds;

export const selectDocumentsIsLoading = (state: State) => state[name].isLoading;

export const makeSelectIsDocumentEnabled = (id: string) =>
  createSelector([selectDocumentsIds], (documentsIds) => {
    const doc = documentsIds?.find((document) => document.id === id);
    return doc ? doc.enabled : false;
  });

export const makeSelectEnabledDocumentsByType = (typePrefix: string) =>
  createSelector([selectDocumentsIds], (documentsIds) => {
    return documentsIds?.filter((doc) => doc.id.startsWith(`${typePrefix}-`) && doc.enabled) || [];
  });
